import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  TextInput,
  Dimensions,
  Platform,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { CheckInService, PlanService, AuthService, UserService } from '../../services/ApiService';
import { useNavigation } from '@react-navigation/native';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const CheckInScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();

  // Debug navigation availability
  useEffect(() => {
    console.log('CheckInScreen: Navigation object available:', !!navigation);
    console.log('CheckInScreen: Navigation methods:', navigation ? Object.keys(navigation) : 'No navigation');
  }, [navigation]);
  const [eligibility, setEligibility] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);
  const [checkInData, setCheckInData] = useState({
    weight: '',
    sleep: 7,
    stress: 5,
    digestion: 5,
    soreness: 5,
    energy: 5,
    mood: 5,
    notes: '',
    photoUrl: '',
    photos: [],
    planChangeRequest: '',
  });

  useEffect(() => {
    loadEligibility();
  }, []);

  useEffect(() => {
    if (user && user.approvedAt) {
      const approvedDate = new Date(user.approvedAt);
      const currentDate = new Date();
      const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // Approx 30 days

      if (currentDate - approvedDate > oneMonthInMs) {
        setIsRestricted(true);
      }
    }
  }, [user]);

  // Handle navigation when shouldNavigate becomes true
  useEffect(() => {
    if (shouldNavigate) {
      console.log('shouldNavigate is true, attempting navigation...');
      setTimeout(() => {
        try {
          if (navigation.navigate) {
            navigation.navigate('Home'); // Corrected from Dashboard to Home
          } else {
            navigation.reset({
              index: 0,
              routes: [{ name: 'Home' }],
            });
          }
        } catch (navError) {
          console.error('Navigation error:', navError);
        }
        setShouldNavigate(false); // Reset the flag
      }, 200);
    }
  }, [shouldNavigate, navigation]);

  const loadEligibility = async () => {
    try {
      const [eligibilityData, userData] = await Promise.all([
        CheckInService.getCheckInEligibility(),
        UserService.getProfile()
      ]);
      setEligibility(eligibilityData);
      setUser(userData);
    } catch (error) {
      console.error('Eligibility check error:', error);
    } finally {
      setLoading(false);
    }
  };


  const requestCameraPermission = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Camera permission is needed to take progress photos');
      return false;
    }
    return true;
  };

  const takePhoto = async () => {
    const hasPermission = await requestCameraPermission();
    if (!hasPermission) return;

    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [3, 4],
        quality: 0.8,
      });

      if (!result.canceled) {
        const newPhoto = {
          uri: result.assets[0].uri,
          caption: '',
          uploadedAt: new Date().toISOString()
        };
        setCheckInData({
          ...checkInData,
          photos: [...checkInData.photos, newPhoto]
        });
      }
    } catch (error) {
      console.error('Camera error:', error);
      Alert.alert('Error', 'Failed to take photo');
    }
  };

  const selectPhoto = async () => {
    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [3, 4],
        quality: 0.8,
        allowsMultipleSelection: true,
      });

      if (!result.canceled) {
        const newPhotos = result.assets.map(asset => ({
          uri: asset.uri,
          caption: '',
          uploadedAt: new Date().toISOString()
        }));
        setCheckInData({
          ...checkInData,
          photos: [...checkInData.photos, ...newPhotos]
        });
      }
    } catch (error) {
      console.error('Photo selection error:', error);
      Alert.alert('Error', 'Failed to select photo');
    }
  };

  const removePhoto = (index) => {
    const updatedPhotos = checkInData.photos.filter((_, i) => i !== index);
    setCheckInData({ ...checkInData, photos: updatedPhotos });
  };

  const updatePhotoCaption = (index, caption) => {
    const updatedPhotos = [...checkInData.photos];
    updatedPhotos[index].caption = caption;
    setCheckInData({ ...checkInData, photos: updatedPhotos });
  };

  const handleSubmit = async () => {
    if (!checkInData.weight) {
      Alert.alert('Error', 'Please enter your current weight');
      return;
    }

    console.log('Starting check-in submission...');
    setSubmitting(true);
    try {
      // Submit check-in first
      console.log('Submitting check-in data...');
      const checkInResponse = await CheckInService.submitCheckIn(checkInData);
      console.log('Check-in submitted successfully:', checkInResponse);

      // Upload photos if any
      if (checkInData.photos.length > 0 && checkInResponse.checkInId) {
        try {
          await CheckInService.uploadCheckInPhotos(checkInResponse.checkInId, checkInData.photos);
        } catch (photoError) {
          console.error('Photo upload error:', photoError);
          Alert.alert('Warning', 'Check-in submitted but photos failed to upload. Please try uploading photos again.');
        }
      }

      // Reset form
      setCheckInData({
        weight: '',
        sleep: 7,
        stress: 5,
        digestion: 5,
        soreness: 5,
        energy: 5,
        mood: 5,
        notes: '',
        photoUrl: '',
        photos: [],
        planChangeRequest: '',
      });

      // Show success message and navigate
      console.log('Check-in completed, showing success alert...');

      // Trigger navigation using state
      console.log('Setting shouldNavigate to true...');
      setShouldNavigate(true);

      // Show success alert (non-blocking)
      Alert.alert(
        'Success',
        'Check-in submitted successfully! Redirecting to dashboard...',
        [
          {
            text: 'OK',
            onPress: () => {
              console.log('User acknowledged success message');
            }
          }
        ]
      );

      // Also reload eligibility in the background
      loadEligibility();
    } catch (error) {
      console.error('Submit check-in error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit check-in. Please try again.';
      Alert.alert('Notice', errorMessage);
    } finally {
      console.log('Check-in submission process completed');
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    console.log('CheckInScreen: Logout button pressed');
    try {
      console.log('CheckInScreen: Starting logout process...');
      await AuthService.logout();
      console.log('CheckInScreen: Logout successful - user will be redirected to login');
      // Navigation will be handled by the auth state change in App.js
    } catch (error) {
      console.error('CheckInScreen: Logout error:', error);
      Alert.alert(
        'Logout Failed',
        'Failed to logout. Please try again.\n\nIf the problem persists, please restart the app.',
        [
          { text: 'OK', style: 'default' }
        ]
      );
    }
  };

  const getSliderColor = (value) => {
    if (value <= 3) return '#FF3B30';
    if (value <= 7) return '#FACC15';
    return '#10B981'; // Green for 8, 9, 10
  };

  const renderSlider = (label, value, onChange, min = 1, max = 10) => {
    const sliderColor = getSliderColor(value);
    const sliderWidth = Math.min(SCREEN_WIDTH - 120, 300); // Account for margins and padding, max width
    const percentage = ((value - min) / (max - min)) * 100;
    const thumbPosition = (sliderWidth * percentage / 100) - 12; // -12 to center the 24px thumb

    return (
      <View style={styles.sliderContainer}>
        <Text style={styles.sliderLabel}>{label}</Text>
        <View style={styles.sliderRow}>
          <Text style={styles.sliderMin}>{min}</Text>
          <View style={[styles.sliderWrapper, { width: sliderWidth }]}>
            <View style={styles.sliderTrack}>
              <View
                style={[
                  styles.sliderFill,
                  {
                    width: percentage + '%',
                    backgroundColor: sliderColor
                  }
                ]}
              />
              <View
                style={[
                  styles.sliderThumb,
                  {
                    left: Math.max(0, Math.min(thumbPosition, sliderWidth - 24)),
                    backgroundColor: sliderColor
                  }
                ]}
              />
            </View>
            <View style={styles.sliderButtonsContainer}>
              {Array.from({ length: max - min + 1 }, (_, i) => {
                const pointValue = min + i;
                return (
                  <TouchableOpacity
                    key={i}
                    style={styles.sliderButton}
                    onPress={() => onChange(pointValue)}
                    activeOpacity={0.7}
                  />
                );
              })}
            </View>
          </View>
          <Text style={styles.sliderMax}>{max}</Text>
        </View>
        <View style={styles.sliderValueContainer}>
          <Text style={[styles.sliderValue, { color: sliderColor }]}>
            {Math.round(value)}
          </Text>
          <View style={styles.sliderButtonRow}>
            <TouchableOpacity
              style={[styles.sliderAdjustButton, value <= min && styles.sliderAdjustButtonDisabled]}
              onPress={() => onChange(Math.max(min, value - 1))}
              disabled={value <= min}
            >
              <Ionicons name="remove" size={20} color={value <= min ? '#ccc' : '#3B82F6'} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.sliderAdjustButton, { marginLeft: 8 }, value >= max && styles.sliderAdjustButtonDisabled]}
              onPress={() => onChange(Math.min(max, value + 1))}
              disabled={value >= max}
            >
              <Ionicons name="add" size={20} color={value >= max ? '#ccc' : '#3B82F6'} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={styles.loadingContainer}>
          <Text style={{ color: '#FFFFFF' }}>Loading check-in...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (isRestricted) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={styles.container}>
          <View style={[styles.header, { paddingTop: 0 }]}>
            <Text style={styles.title}>Check-In</Text>
            <Text style={styles.subtitle}>Progress Tracking</Text>
          </View>
          <View style={styles.emptyContainer}>
            <View style={[styles.emptyIconContainer, { backgroundColor: '#0B0E14' }]}>
              <Ionicons name="lock-closed" size={48} color="#FF5252" />
            </View>
            <Text style={styles.emptyTitle}>Access Expired</Text>
            <Text style={styles.emptyText}>
              Your access to this feature has expired because your plan is no longer active.
            </Text>
            <Text style={[styles.emptyText, { marginTop: 10, fontStyle: 'italic', marginBottom: 20 }]}>
              Please renew your plan to continue tracking.
            </Text>
            <TouchableOpacity
              style={[styles.sendButton, { width: 160, borderRadius: 12, height: 44 }]}
              onPress={() => navigation.navigate('Home')}
            >
              <Text style={{ color: 'white', fontWeight: 'bold' }}>Return to Home</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  if (!eligibility.eligible) {
    return (
      <SafeAreaView style={styles.safeArea} edges={['left', 'right']}>
        <View style={styles.container}>
          <View style={[styles.header, { paddingTop: Math.max(insets.top + 8, 16) }]}>
            <Text style={styles.title}>Check-In</Text>
            <Text style={styles.subtitle}>Progress Tracking</Text>
          </View>

          <View style={styles.notEligibleContainer}>
            <Ionicons name="time-outline" size={48} color="#FACC15" />
            <Text style={styles.notEligibleTitle}>Check-in Not Available</Text>
            <Text style={styles.notEligibleText}>
              Your last check-in was within the required frequency for your {eligibility.planType} plan.
            </Text>
            <Text style={styles.notEligibleSubtext}>
              {eligibility.planType === 'basic'
                ? 'Basic plan allows check-ins every 14 days'
                : 'Pro/Human plans allow check-ins every 7 days'
              }
            </Text>
            {eligibility.nextCheckInDate && (
              <Text style={styles.nextCheckInDate}>
                Next check-in: {new Date(eligibility.nextCheckInDate).toLocaleDateString('en-GB')}
              </Text>
            )}

            {/* Latest Reason for Change - Show even when locked */}

            <View style={styles.adminContactContainer}>
              <Ionicons name="mail-outline" size={20} color="#3B82F6" />
              <Text style={styles.adminContactText}>
                Want to change your plan? Contact us at{' '}
                <Text style={styles.adminEmail}>sweatandsnack2024@gmail.com</Text>
              </Text>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <View style={[styles.webContainer, { paddingTop: insets.top }]}>
      {/* <SafeAreaView style={styles.safeArea} edges={['left', 'right']}> */}
      <ScrollView
        style={styles.container}
        contentContainerStyle={[styles.scrollContent]}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View style={[styles.header, { paddingTop: 0 }]}>
          <Text style={styles.title}>Check-In</Text>
          <Text style={styles.subtitle}>Progress Tracking</Text>
        </View>

        {/* Weight Input */}
        <View style={[styles.section, styles.firstSection]}>
          <Text style={styles.sectionTitle}>Current Weight</Text>
          <View style={styles.weightContainer}>
            <TextInput
              style={styles.weightInput}
              placeholderTextColor="#999"
              value={checkInData.weight}
              onChangeText={(value) => setCheckInData({ ...checkInData, weight: value })}
              placeholder="Enter weight (kg)"
              keyboardType="numeric"
            />
            <Text style={styles.weightUnit}>kg</Text>
          </View>
        </View>

        {/* Sleep Hours */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Sleep Hours (on avg)</Text>
          <View style={styles.sleepContainer}>
            <TextInput
              style={styles.sleepInput}
              placeholderTextColor="#999"
              value={checkInData.sleep.toString()}
              onChangeText={(value) => setCheckInData({ ...checkInData, sleep: parseFloat(value) || 0 })}
              keyboardType="numeric"
              placeholder="Hours"
            />
            <Text style={styles.sleepUnit}>hours</Text>
          </View>
        </View>

        {/* Wellness Sliders */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>How are you feeling? (on avg)</Text>

          {renderSlider('Stress Level', checkInData.stress, (value) =>
            setCheckInData({ ...checkInData, stress: value })
          )}

          {renderSlider('Digestion', checkInData.digestion, (value) =>
            setCheckInData({ ...checkInData, digestion: value })
          )}

          {renderSlider('Muscle Soreness', checkInData.soreness, (value) =>
            setCheckInData({ ...checkInData, soreness: value })
          )}

          {renderSlider('Energy Level', checkInData.energy, (value) =>
            setCheckInData({ ...checkInData, energy: value })
          )}

          {renderSlider('Mood', checkInData.mood, (value) =>
            setCheckInData({ ...checkInData, mood: value })
          )}
        </View>

        {/* Progress Photos */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Progress Photos (Optional)</Text>

          {/* Face Cropping Warning */}
          <View style={styles.warningContainer}>
            <Ionicons name="warning" size={20} color="#FACC15" />
            <Text style={styles.warningText}>
              Crop your face and send - For better progress tracking, please crop your face in the photos
            </Text>
          </View>

          <View style={styles.photoContainer}>
            {/* Photo Grid */}
            {checkInData.photos.length > 0 && (
              <View style={styles.photoGrid}>
                {checkInData.photos.map((photo, index) => (
                  <View key={index} style={styles.photoItem}>
                    <View style={styles.photoPreview}>
                      <Text style={styles.photoText}>Photo {index + 1}</Text>
                      <TouchableOpacity
                        style={styles.removePhotoButton}
                        onPress={() => removePhoto(index)}
                      >
                        <Ionicons name="close-circle" size={20} color="#FF3B30" />
                      </TouchableOpacity>
                    </View>
                    <TextInput
                      style={styles.captionInput}
                      placeholderTextColor="#999"
                      value={photo.caption}
                      onChangeText={(text) => updatePhotoCaption(index, text)}
                      placeholder="Add caption (optional)"
                      multiline
                    />
                  </View>
                ))}
              </View>
            )}

            {/* Add Photo Buttons */}
            {checkInData.photos.length < 5 && (
              <View style={styles.photoButtons}>
                <TouchableOpacity style={styles.photoButton} onPress={takePhoto}>
                  <Ionicons name="camera" size={24} color="#3B82F6" />
                  <Text style={styles.photoButtonText}>Take Photo</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.photoButton} onPress={selectPhoto}>
                  <Ionicons name="image" size={24} color="#3B82F6" />
                  <Text style={styles.photoButtonText}>Select Photos</Text>
                </TouchableOpacity>
              </View>
            )}

            {checkInData.photos.length >= 5 && (
              <Text style={styles.maxPhotosText}>Maximum 5 photos allowed</Text>
            )}
          </View>
        </View>

        {/* Notes */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Additional Notes</Text>
          <TextInput
            style={styles.notesInput}
            placeholderTextColor="#999"
            value={checkInData.notes}
            onChangeText={(value) => setCheckInData({ ...checkInData, notes: value })}
            placeholder="How has your week been? Any concerns or achievements?"
            multiline
            numberOfLines={4}
          />
        </View>

        {/* Latest Reason for Change */}

        {/* Plan Change Request */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Plan Change Request (Optional)</Text>
          <TextInput
            style={styles.notesInput}
            placeholderTextColor="#999"
            value={checkInData.planChangeRequest}
            onChangeText={(value) => setCheckInData({ ...checkInData, planChangeRequest: value })}
            placeholder="Any specific changes you'd like to your workout or diet plan?"
            multiline
            numberOfLines={3}
          />
        </View>

        {/* Submit Button */}
        <View style={styles.submitContainer}>
          <TouchableOpacity
            style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={submitting}
          >
            <Text style={styles.submitButtonText}>
              {submitting ? 'Submitting...' : 'Submit Check-In'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
      {/* </SafeAreaView> */}
    </View>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B0E14',
    paddingBottom: 0,
    marginBottom: 0,
  },
  webContainer: {
    flex: 1,
    backgroundColor: '#0B0E14', // Background color
    paddingBottom: 0, // No extra padding at the bottom on PWA
  },
  container: {
    flex: 1,
    backgroundColor: '#0B0E14',
  },
  scrollContent: {
    paddingBottom: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: SCREEN_HEIGHT * 0.7,
  },
  header: {
    backgroundColor: '#161C2D',
    paddingHorizontal: Math.max(16, SCREEN_WIDTH * 0.05),
    // paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2C365E',
    shadowcolor: '#FFFFFF',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoutButton: {
    padding: 8,
    borderRadius: 8,
    backgroundColor: '#0B0E14',
  },
  title: {
    fontSize: Math.max(24, SCREEN_WIDTH * 0.07),
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.04),
    color: '#6D759E',
    marginTop: 4,
  },
  firstSection: {
    marginTop: 16,
  },
  section: {
    backgroundColor: '#161C2D',
    margin: Math.max(12, SCREEN_WIDTH * 0.04),
    marginTop: 0,
    borderRadius: 20,
    padding: Math.max(16, SCREEN_WIDTH * 0.05),
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1,
    borderColor: '#f1f5f9',
  },
  sectionTitle: {
    fontSize: Math.max(18, SCREEN_WIDTH * 0.055),
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 20,
    letterSpacing: -0.5,
  },
  weightContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#0B0E14',
    padding: Math.max(14, SCREEN_WIDTH * 0.04),
    borderRadius: 16,
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    marginRight: 12,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    color: '#FFFFFF',
    fontWeight: '500',
    minHeight: 50,
  },
  weightUnit: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    color: '#6D759E',
  },
  sleepContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sleepInput: {
    flex: 1,
    backgroundColor: '#0B0E14',
    color: '#FFFFFF',
    padding: Math.max(14, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    marginRight: 12,
    minHeight: 50,
  },
  sleepUnit: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    color: '#6D759E',
  },
  sliderContainer: {
    marginBottom: Math.max(20, SCREEN_HEIGHT * 0.025),
  },
  sliderLabel: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    fontWeight: '500',
    color: '#FFFFFF',
    marginBottom: 12,
  },
  sliderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  sliderMin: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.035),
    color: '#6D759E',
    width: Math.max(20, SCREEN_WIDTH * 0.06),
    textAlign: 'left',
  },
  sliderMax: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.035),
    color: '#6D759E',
    width: Math.max(20, SCREEN_WIDTH * 0.06),
    textAlign: 'right',
  },
  sliderWrapper: {
    flex: 1,
    marginHorizontal: 8,
    height: 50,
    justifyContent: 'center',
  },
  sliderTrack: {
    height: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 3,
    position: 'relative',
    marginBottom: 8,
  },
  sliderFill: {
    height: '100%',
    borderRadius: 3,
    position: 'absolute',
    left: 0,
    top: 0,
  },
  sliderThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    position: 'absolute',
    top: -9,
    marginLeft: -12,
    borderWidth: 3,
    borderColor: '#fff',
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  sliderButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute',
    width: '100%',
    top: 20,
  },
  sliderButton: {
    flex: 1,
    height: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sliderValueContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
  },
  sliderValue: {
    fontSize: Math.max(20, SCREEN_WIDTH * 0.06),
    fontWeight: 'bold',
    textAlign: 'center',
    minWidth: 40,
  },
  sliderButtonRow: {
    flexDirection: 'row',
  },
  sliderAdjustButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#0B0E14',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#2C365E',
  },
  sliderAdjustButtonDisabled: {
    opacity: 0.5,
  },
  photoContainer: {
    alignItems: 'center',
  },
  photoPreview: {
    backgroundColor: '#0B0E14',
    padding: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 12,
    alignItems: 'center',
    position: 'relative',
    minHeight: 100,
  },
  photoText: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.04),
    color: '#FFFFFF',
  },
  removePhotoButton: {
    position: 'absolute',
    top: -8,
    right: -8,
  },
  photoButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  photoButton: {
    backgroundColor: '#0B0E14',
    padding: Math.max(18, SCREEN_WIDTH * 0.05),
    borderRadius: 16,
    alignItems: 'center',
    flex: 1,
    marginHorizontal: 4,
    borderWidth: 2,
    borderColor: '#e2e8f0',
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 80,
  },
  photoButtonText: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.035),
    color: '#3B82F6',
    marginTop: 8,
    fontWeight: '600',
  },
  notesInput: {
    backgroundColor: '#0B0E14',
    padding: Math.max(14, SCREEN_WIDTH * 0.04),
    borderRadius: 16,
    fontSize: Math.max(14, SCREEN_WIDTH * 0.045),
    textAlignVertical: 'top',
    minHeight: Math.max(100, SCREEN_HEIGHT * 0.12),
    borderWidth: 2,
    borderColor: '#e2e8f0',
    color: '#FFFFFF',
    fontWeight: '400',
  },
  submitContainer: {
    padding: Math.max(16, SCREEN_WIDTH * 0.05),
    marginBottom: 10,
  },
  submitButton: {
    backgroundColor: '#3B82F6',
    padding: Math.max(18, SCREEN_WIDTH * 0.05),
    borderRadius: 16,
    alignItems: 'center',
    shadowColor: '#3B82F6',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
    minHeight: 56,
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButtonText: {
    color: 'white',
    fontSize: Math.max(16, SCREEN_WIDTH * 0.05),
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  notEligibleContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: Math.max(30, SCREEN_WIDTH * 0.08),
    backgroundColor: '#161C2D',
    margin: Math.max(16, SCREEN_WIDTH * 0.04),
    borderRadius: 16,
  },
  notEligibleTitle: {
    fontSize: Math.max(18, SCREEN_WIDTH * 0.055),
    fontWeight: '600',
    color: '#FFFFFF',
    marginTop: 16,
    marginBottom: 8,
    textAlign: 'center',
  },
  notEligibleText: {
    fontSize: Math.max(14, SCREEN_WIDTH * 0.042),
    color: '#6D759E',
    textAlign: 'center',
    marginBottom: 8,
  },
  notEligibleSubtext: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.038),
    color: '#6D759E',
    textAlign: 'center',
    marginBottom: 16,
  },
  nextCheckInDate: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.038),
    color: '#3B82F6',
    fontWeight: '500',
  },
  adminContactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0B0E14',
    padding: Math.max(10, SCREEN_WIDTH * 0.03),
    borderRadius: 8,
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#2C365E',
    width: '100%',
  },
  adminContactText: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.038),
    color: '#FFFFFF',
    marginLeft: 8,
    flex: 1,
  },
  adminEmail: {
    color: '#3B82F6',
    fontWeight: '600',
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0B0E14',
    padding: Math.max(10, SCREEN_WIDTH * 0.03),
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#FFEAA7',
  },
  warningText: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.038),
    color: '#856404',
    marginLeft: 8,
    flex: 1,
    fontWeight: '500',
  },
  photoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 16,
    width: '100%',
  },
  photoItem: {
    width: SCREEN_WIDTH < 400 ? '100%' : '48%',
    marginBottom: 12,
  },
  captionInput: {
    backgroundColor: '#0B0E14',
    color: '#FFFFFF',
    padding: 8,
    borderRadius: 8,
    fontSize: Math.max(11, SCREEN_WIDTH * 0.033),
    marginTop: 8,
    textAlignVertical: 'top',
    minHeight: 50,
    borderWidth: 1,
    borderColor: '#2C365E',
  },
  maxPhotosText: {
    fontSize: Math.max(12, SCREEN_WIDTH * 0.038),
    color: '#6D759E',
    textAlign: 'center',
    fontStyle: 'italic',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    marginTop: 60,
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#e0e7ff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#FFFFFF',
  },
  emptyText: {
    textAlign: 'center',
    color: '#6D759E',
    fontSize: 14,
  },
  sendButton: {
    backgroundColor: '#3B82F6',
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    shadowcolor: '#FFFFFF',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
});

export default CheckInScreen;
